// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Enhanced Warp Event Manager
 * 
 * Handles both immediate and interactive events with proper chain management
 * Replaces the original WarpEventManager with a more sophisticated system
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IMMEDIATE_EVENTS, INTERACTIVE_EVENTS } from '../config/EnhancedEventsConfig';
import RandomEventManager from '../utils/game/RandomEventManager';
import CrewConversationManager from '../utils/game/CrewConversationManager';
import GameAlert from '../utils/ui/GameAlertUtils';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import AlienEncounterModal from './AlienEncounterModal';
import AlienManager from '../utils/game/AlienManager';
import { useUniverse } from '../contexts/UniverseContext';
import EventModal from './EventModal';
import MiniGameLauncher from './MiniGameLauncher';
import './EnhancedEventNotification.css';
import { getDifficultyMultipliers } from '../utils/DifficultyUtils';

const EnhancedWarpEventManager = ({
  fuel,
  removeFuel,
  addCredits,
  removeCredits,
  damageSystem,
  getSystemHealth,
  selectedWarpFactor,
  onSetWarpFactor,
  onGravityEffect,
  refuelToFull,
  fuelDepleted,
  inCombat,
  currentLocation,
  warpTravelProgress,
  warpTravelTime,
  onCombatInterrupt,
  getDisplayDistance,
  getDefaultWarpFactor,
  credits,
  weapons = [],
  setWarpPaused
}) => {
  // Event state
  const [activeEvent, setActiveEvent] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  
  // Modal state
  const [modalFading, setModalFading] = useState(false);
  
  // Mini-game state
  const [showAsteroidMiniGame, setShowAsteroidMiniGame] = useState(false);
  const [showCombatMiniGame, setShowCombatMiniGame] = useState(false);
  const [miniGameConfig, setMiniGameConfig] = useState(null);
  
  // Alien encounter state
  const [showAlienModal, setShowAlienModal] = useState(false);
  const [alienEncounter, setAlienEncounter] = useState(null);
  
  // Get universe context
  const { getCurrentUniverseData } = useUniverse();
  
  // Debug logging disabled for performance
  //   showCombatMiniGame,
  //   showAsteroidMiniGame,
  //   miniGameConfig: !!miniGameConfig
  // });
  
  // Quantum storm effect state
  const [showQuantumStorm, setShowQuantumStorm] = useState(false);
  const [quantumStormIntensity, setQuantumStormIntensity] = useState(1.0);
  
  // Refs for cleanup
  const feedbackTimeoutRef = useRef(null);
  const chainTimeoutRef = useRef(null);

  // Helper functions
  const isLowFuel = useCallback(() => fuel < 20, [fuel]);

  // Create context object for event handlers
  const eventContext = useCallback(() => ({
    fuel,
    removeFuel,
    addCredits,
    removeCredits,
    damageSystem,
    getSystemHealth,
    selectedWarpFactor,
    onSetWarpFactor,
    onGravityEffect,
    refuelToFull,
    isLowFuel,
    setActionFeedback,
    credits,
    currentLocation,
    warpTravelProgress,
    warpTravelTime,
    getDisplayDistance,
    getDefaultWarpFactor
  }), [fuel, removeFuel, addCredits, removeCredits, damageSystem, getSystemHealth, 
       selectedWarpFactor, onSetWarpFactor, onGravityEffect, refuelToFull, isLowFuel,
       credits, currentLocation, warpTravelProgress, warpTravelTime, getDisplayDistance, getDefaultWarpFactor]);

  // Generate random event based on new system
  const generateRandomEvent = useCallback(() => {
    const allEvents = { ...IMMEDIATE_EVENTS, ...INTERACTIVE_EVENTS };
    const eventTypes = Object.keys(allEvents);
    const randomType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    
    return allEvents[randomType];
  }, []);

  // Handle event action - SIMPLIFIED
  const handleEventAction = async (action) => {
    if (isProcessingAction) return;
    
    setIsProcessingAction(true);

    // Show immediate feedback
    if (action.feedback) {
      setActionFeedback({
        message: action.feedback,
        color: activeEvent.color
      });
    }

    // Execute the action effect
    if (action.effect) {
      const context = eventContext();
      action.effect(context);
    }

    // Handle ALIEN_ENCOUNTER special case - trigger AlienEncounterModal
    if (activeEvent.type === 'ALIEN_ENCOUNTER' && action.label === 'OPEN COMMUNICATIONS') {
      
      // Show feedback to hide buttons immediately
      if (action.feedback) {
        setActionFeedback({
          message: action.feedback,
          color: activeEvent.color
        });
      }
      
      // Generate random alien encounter
      const universeData = getCurrentUniverseData();
      const universeAliens = universeData?.aliens || null;
      
      if (universeAliens && universeAliens.randomEncounters && universeAliens.randomEncounters.length > 0) {
        const randomAlienId = universeAliens.randomEncounters[Math.floor(Math.random() * universeAliens.randomEncounters.length)];
        const encounter = AlienManager.generateEncounter(randomAlienId, {
          encounterType: 'warp_travel',
          location: 'deep_space'
        });
        
        if (encounter) {
          // Trigger alien modal after brief delay to show feedback
          setTimeout(() => {
            setAlienEncounter(encounter);
            setShowAlienModal(true);
          }, 500);
        } else {
          console.warn('[EnhancedWarpEventManager] Failed to generate alien encounter');
          // Fallback to simple action
          setTimeout(() => {
            closeEventModal();
            RandomEventManager.clearCurrentEvent();
          }, 2000);
        }
      } else {
        console.warn('[EnhancedWarpEventManager] No aliens available in current universe');
        // Fallback to simple action
        setTimeout(() => {
          closeEventModal();
          RandomEventManager.clearCurrentEvent();
        }, 2000);
      }
    }
    // Handle new alien encounter types (distress, first contact, ambush, trade convoy, betrayal, cultural exchange)
    else if (
      (activeEvent.type === 'ALIEN_DISTRESS_CALL' && (action.label === 'RENDER ASSISTANCE' || action.label === 'INVESTIGATE CAUTIOUSLY')) ||
      (activeEvent.type === 'ALIEN_FIRST_CONTACT' && action.label === 'INITIATE CONTACT') ||
      (activeEvent.type === 'ALIEN_AMBUSH' && (action.label === 'DEFENSIVE POSTURE' || action.label === 'ATTEMPT COMMUNICATION')) ||
      (activeEvent.type === 'ALIEN_TRADE_CONVOY' && (action.label === 'APPROACH CONVOY' || action.label === 'REQUEST ESCORT')) ||
      (activeEvent.type === 'ALIEN_BETRAYAL' && action.label === 'HELP THEM') ||
      (activeEvent.type === 'CULTURAL_EXCHANGE' && action.label === 'PARTICIPATE') ||
      (activeEvent.type === 'TERRITORIAL_DISPUTE' && (action.label === 'SIDE WITH FACTION A' || action.label === 'SIDE WITH FACTION B'))
    ) {
      // Show feedback to hide buttons immediately
      if (action.feedback) {
        setActionFeedback({
          message: action.feedback,
          color: activeEvent.color
        });
      }
      
      // Generate alien encounter based on event type
      const universeData = getCurrentUniverseData();
      const universeAliens = universeData?.aliens || null;
      
      if (universeAliens && universeAliens.randomEncounters && universeAliens.randomEncounters.length > 0) {
        const randomAlienId = universeAliens.randomEncounters[Math.floor(Math.random() * universeAliens.randomEncounters.length)];
        
        // Determine encounter context based on event type
        let encounterContext = {
          encounterType: 'warp_travel',
          location: 'deep_space'
        };
        
        if (activeEvent.type === 'ALIEN_DISTRESS_CALL') {
          encounterContext.encounterType = 'distress_call';
          encounterContext.helpNeeded = true;
        } else if (activeEvent.type === 'ALIEN_FIRST_CONTACT') {
          encounterContext.encounterType = 'first_contact';
          encounterContext.newSpecies = true;
        } else if (activeEvent.type === 'ALIEN_AMBUSH') {
          encounterContext.encounterType = 'ambush';
          encounterContext.hostile = true;
        } else if (activeEvent.type === 'ALIEN_TRADE_CONVOY') {
          encounterContext.encounterType = 'trade_convoy';
          encounterContext.tradeFocused = true;
        } else if (activeEvent.type === 'ALIEN_BETRAYAL') {
          encounterContext.encounterType = 'betrayal';
          encounterContext.suspicious = true;
          encounterContext.hostile = Math.random() < 0.6; // 60% chance it's actually hostile
        } else if (activeEvent.type === 'CULTURAL_EXCHANGE') {
          encounterContext.encounterType = 'cultural_exchange';
          encounterContext.peaceful = true;
        } else if (activeEvent.type === 'TERRITORIAL_DISPUTE') {
          encounterContext.encounterType = 'territorial_dispute';
          encounterContext.faction = action.label.includes('FACTION A') ? 'A' : 'B';
        }
        
        const encounter = AlienManager.generateEncounter(randomAlienId, encounterContext);
        
        if (encounter) {
          // Trigger alien modal after brief delay to show feedback
          setTimeout(() => {
            setAlienEncounter(encounter);
            setShowAlienModal(true);
          }, 500);
        } else {
          console.warn('[EnhancedWarpEventManager] Failed to generate alien encounter');
          setTimeout(() => {
            closeEventModal();
            RandomEventManager.clearCurrentEvent();
          }, 2000);
        }
      } else {
        console.warn('[EnhancedWarpEventManager] No aliens available in current universe');
        setTimeout(() => {
          closeEventModal();
          RandomEventManager.clearCurrentEvent();
        }, 2000);
      }
    }
    // Handle system triggers (mini-games, combat)
    else if (action.triggersSystem) {
      
      if (action.triggersSystem === 'COMBAT_SYSTEM' && onCombatInterrupt) {
        closeEventModal();
        RandomEventManager.clearCurrentEvent(); // Clear event and resume generation
        onCombatInterrupt(action.systemData || { enemyType: 'pirates', enemyCount: 2 });
      } else if (action.triggersSystem === 'ASTEROID_MINIGAME') {
        // Trigger mini-game - DON'T touch modalFading or activeEvent
        // The mini-game will render instead of the modal
        triggerAsteroidMiniGame(action.systemData || { difficulty: 'normal', duration: 45 });
      } else if (action.triggersSystem === 'COMBAT_MINIGAME') {
        // Trigger mini-game - DON'T touch modalFading or activeEvent
        // The mini-game will render instead of the modal
        triggerCombatMiniGame(action.systemData || { 
          enemies: [
            { type: 'SCOUT', name: 'HOSTILE-ALPHA', shields: 50, hull: 50, speed: 1.5 }
          ]
        });
      }
    } else {
      // Simple action - close modal and resume events
      setTimeout(() => {
        closeEventModal();
        RandomEventManager.clearCurrentEvent(); // Clear event and resume generation
      }, 2000);
    }

    setIsProcessingAction(false);
  };

  // Notify that warp should resume (called by external systems or immediately for quick events)
  const notifyWarpResume = useCallback(() => {
    // Emit event to notify NavigationPanel to resume warp/fuel/events
    EventBus.emit(EVENT_TYPES.EVENT_SYSTEM_RESUME, { source: 'EnhancedWarpEventManager' });
  }, []);

  // Trigger asteroid mini-game
  const triggerAsteroidMiniGame = useCallback((systemData) => {
    setMiniGameConfig(systemData);
    setShowAsteroidMiniGame(true);
    
    // Warp is already paused from event generation, no need to pause again
  }, []);

  // Handle asteroid mini-game completion
  const handleAsteroidMiniGameComplete = useCallback((finalStats) => {
    setShowAsteroidMiniGame(false);
    setMiniGameConfig(null);
    
    // Apply rewards based on performance
    const context = eventContext();
    if (finalStats.damage < 25) {
      // Excellent performance - bonus credits
      context.addCredits(100);
      GameAlert.success('🏆 ASTEROID FIELD NAVIGATED - Excellent piloting! +100 credits');
      setTimeout(() => {
        context.setActionFeedback({
          message: '🏆 EXCELLENT PILOTING: +100 credits bonus!',
          color: '#00ff88'
        });
      }, 1000);
    } else if (finalStats.damage < 50) {
      // Good performance - small bonus
      context.addCredits(50);
      GameAlert.success('👍 ASTEROID FIELD NAVIGATED - Good piloting! +50 credits');
      setTimeout(() => {
        context.setActionFeedback({
          message: '👍 GOOD PILOTING: +50 credits bonus',
          color: '#ffaa00'
        });
      }, 1000);
    } else {
      GameAlert.info('✓ ASTEROID FIELD NAVIGATED - Minor hull damage sustained');
    }
    
    // Resume event generation and warp travel after mini-game
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent(); // This will resume event generation and call setWarpPaused(false)
    }, 2000);
  }, [eventContext]);

  // Handle asteroid mini-game failure
  const handleAsteroidMiniGameOver = useCallback((finalStats) => {
    setShowAsteroidMiniGame(false);
    setMiniGameConfig(null);
    
    // Apply damage based on performance
    const context = eventContext();
    const hullDamage = Math.floor(finalStats.damage * 0.5); // Convert percentage to HP damage
    if (hullDamage > 0) {
      context.damageSystem('HULL', hullDamage);
      GameAlert.critical(`💥 ASTEROID COLLISION - ${hullDamage} HP hull damage sustained!`);
      setTimeout(() => {
        context.setActionFeedback({
          message: `💥 NAVIGATION FAILED: ${hullDamage} HP hull damage from impacts!`,
          color: '#ff4444'
        });
      }, 1000);
    }
    
    // Resume event generation and warp travel after mini-game
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent(); // This will resume event generation and call setWarpPaused(false)
    }, 2000);
  }, [eventContext]);

  // Trigger combat mini-game
  const triggerCombatMiniGame = useCallback((systemData) => {
    setMiniGameConfig(systemData);
    setShowCombatMiniGame(true);
    
    // Warp is already paused from event generation, no need to pause again
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCombatMiniGame, showAsteroidMiniGame, activeEvent, modalFading]);

  // Handle combat mini-game victory
  const handleCombatMiniGameVictory = useCallback((finalStats) => {
    setShowCombatMiniGame(false);
    setMiniGameConfig(null);
    
    // Apply rewards based on performance
    const context = eventContext();
    const creditsReward = finalStats.enemiesDestroyed * 100;
    context.addCredits(creditsReward);
    
    GameAlert.success(`🏆 COMBAT VICTORY - ${finalStats.enemiesDestroyed} enemies destroyed! +${creditsReward} credits`);
    
    // Trigger crew conversation about combat victory
    CrewConversationManager.triggerEventConversation('combat_victory', {
      enemiesDestroyed: finalStats.enemiesDestroyed,
      creditsReward: creditsReward
    });
    
    setTimeout(() => {
      context.setActionFeedback({
        message: `🏆 COMBAT VICTORY: +${creditsReward} credits!`,
        color: '#00ff88'
      });
    }, 1000);
    
    // Resume event generation and warp travel after mini-game
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent(); // This will resume event generation and call setWarpPaused(false)
    }, 2000);
  }, [eventContext]);

  // Handle combat mini-game defeat
  const handleCombatMiniGameDefeat = useCallback((finalStats) => {
    setShowCombatMiniGame(false);
    setMiniGameConfig(null);
    
    // Apply penalties
    const context = eventContext();
    const repairCost = Math.floor(finalStats.damageTaken * 10 * getDifficultyMultipliers().repairCostMultiplier);
    context.removeCredits(repairCost);
    
    GameAlert.critical(`💥 COMBAT DEFEAT - Ship damaged! -${repairCost} credits for repairs`);
    
    setTimeout(() => {
      context.setActionFeedback({
        message: `💥 COMBAT DEFEAT: -${repairCost} credits for repairs`,
        color: '#ff4444'
      });
    }, 1000);
    
    // Resume event generation and warp travel after mini-game
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent(); // This will resume event generation and call setWarpPaused(false)
    }, 2000);
  }, [eventContext]);

  // Handle combat mini-game close (emergency exit)
  const handleCombatMiniGameClose = useCallback(() => {
    setShowCombatMiniGame(false);
    setMiniGameConfig(null);
    
    // Resume event generation and warp travel
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent(); // This will resume event generation and call setWarpPaused(false)
    }, 500);
  }, []);

  // Handle alien encounter modal close
  const handleAlienModalClose = useCallback(() => {
    setShowAlienModal(false);
    setAlienEncounter(null);
    
    // Resume warp travel - event modal is already closed
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent();
    }, 500);
  }, []);

  // Handle alien encounter outcome
  const handleAlienOutcome = useCallback((outcome) => {
    
    // Outcome is already processed by AlienEncounterModal (credits, damage, reputation)
    // Just show a summary alert
    if (outcome.success) {
      GameAlert.success(outcome.message);
    } else {
      GameAlert.warning(outcome.message);
    }
    
    // Close modal and resume - event modal is already closed
    setShowAlienModal(false);
    setAlienEncounter(null);
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent();
    }, 500);
  }, []);

  // Handle mini-game close (emergency exit)
  const handleAsteroidMiniGameClose = useCallback(() => {
    setShowAsteroidMiniGame(false);
    setMiniGameConfig(null);
    
    // Apply moderate damage for early exit
    const context = eventContext();
    context.damageSystem('HULL', 15);
    setTimeout(() => {
      context.setActionFeedback({
        message: '⚠️ EMERGENCY EXIT: 15 HP hull damage from asteroid impacts',
        color: '#ffaa00'
      });
    }, 1000);
    
    // Resume event generation and warp travel after mini-game
    setTimeout(() => {
      RandomEventManager.clearCurrentEvent(); // This will resume event generation and call setWarpPaused(false)
    }, 2000);
  }, [eventContext]);

  // Expose notifyWarpResume for external systems
  React.useEffect(() => {
    // Store reference globally so external systems can call it
    window.EnhancedWarpEventManager = {
      notifyWarpResume
    };
    
    return () => {
      delete window.EnhancedWarpEventManager;
    };
  }, [notifyWarpResume]);

  // Close event modal with fade animation - SIMPLIFIED
  const closeEventModal = () => {
    setModalFading(true);
    setTimeout(() => {
      setActiveEvent(null);
      setActionFeedback(null);
      setModalFading(false);
      // Clear quantum storm effect when event is cleared
      setShowQuantumStorm(false);
    }, 300);
  };

  // Clear feedback after timeout
  useEffect(() => {
    if (actionFeedback) {
      feedbackTimeoutRef.current = setTimeout(() => {
        setActionFeedback(null);
      }, 4000);
    }
    
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, [actionFeedback]);

  // Initialize RandomEventManager with new generator
  useEffect(() => {
    RandomEventManager.setEventGenerator(generateRandomEvent);
  }, [generateRandomEvent]);

  // Listen for events from RandomEventManager and cheat system
  useEffect(() => {
    const handleEventGenerated = (event) => {
      
      // Dispatch alert for event
      GameAlert.warning(`⚠️ ${event.title}`);
      
      // Play voice announcements for specific event types
      if (event.type === 'QUANTUM_STORM') {
        EventBus.emit(EVENT_TYPES.QUANTUM_STORM_WARNING);
      } else if (event.type === 'ASTEROID_FIELD') {
        EventBus.emit(EVENT_TYPES.ASTEROID_FIELD_WARNING);
      } else if (event.type.includes('COLLISION')) {
        EventBus.emit(EVENT_TYPES.COLLISION_WARNING);
      }
      
      // CRITICAL: Pause warp travel when event fires
      if (setWarpPaused) {
        setWarpPaused(true);
      }
      
      // CRITICAL: Dispatch event to NavigationPanel to pause warp travel
      EventBus.emit(EVENT_TYPES.WARP_EVENT_GENERATED, { event });
      
      // Trigger quantum storm visual effect for quantum storm events
      if (event.type === 'QUANTUM_STORM') {
        setQuantumStormIntensity(1.0 + Math.random() * 0.5); // Random intensity 1.0-1.5
        setShowQuantumStorm(true);
      }
      
      setActiveEvent(event);
    };

    const handleEventCleared = () => {
      
      // CRITICAL: Resume warp travel when event is cleared
      if (setWarpPaused) {
        setWarpPaused(false);
      }
      
      // CRITICAL: Emit event to NavigationPanel to resume warp travel
      EventBus.emit(EVENT_TYPES.WARP_EVENT_CLEARED);
      
      setActiveEvent(null);
      setActionFeedback(null);
      
      // Clear quantum storm effect when event is cleared
      setShowQuantumStorm(false);
    };

    const handleCheatEventTriggered = (data) => {
      const { eventType } = data;
      
      // Find the event in our config
      const allEvents = { ...IMMEDIATE_EVENTS, ...INTERACTIVE_EVENTS };
      const event = allEvents[eventType];
      
      if (event) {
        // Trigger the event as if it was generated naturally
        handleEventGenerated(event);
      } else {
        console.warn('[EnhancedWarpEventManager] Unknown event type from cheat:', eventType);
      }
    };

    RandomEventManager.addEventListener('eventGenerated', handleEventGenerated);
    RandomEventManager.addEventListener('eventCleared', handleEventCleared);
    EventBus.on(EVENT_TYPES.CHEAT_EVENT_TRIGGERED, handleCheatEventTriggered);

    return () => {
      RandomEventManager.removeEventListener('eventGenerated', handleEventGenerated);
      RandomEventManager.removeEventListener('eventCleared', handleEventCleared);
      EventBus.off('CHEAT_EVENT_TRIGGERED', handleCheatEventTriggered);
    };
  }, [setWarpPaused]);

  // Handle fuel depletion - trigger tow offer event (only once per fuel depletion)
  const fuelDepletionHandledRef = useRef(false);
  
  useEffect(() => {
    if (fuelDepleted && !activeEvent && !fuelDepletionHandledRef.current) {
      fuelDepletionHandledRef.current = true; // Mark as handled to prevent repeats
      
      // Create fuel depletion event that works with the existing system
      const fuelDepletionEvent = {
        type: 'FUEL_DEPLETED',
        title: '⚠️ FUEL DEPLETED',
        message: 'Antimatter reserves exhausted! Ship has dropped out of warp.\n\n🆓 FREE TOW SERVICE AVAILABLE',
        color: '#ff4444',
        actions: [
          {
            label: 'ACCEPT FREE TOW',
            feedback: '📡 DISTRESS SIGNAL SENT - Tow ship en route',
            effect: () => {
              // Emit the tow service event
              EventBus.emit(EVENT_TYPES.ACCEPT_TOW_SERVICE);
              
              // Close the modal immediately
              setTimeout(() => {
                closeEventModal();
              }, 1000); // Give time for feedback to show
            }
          },
          {
            label: 'DECLINE (CONTINUE)',
            feedback: '🛸 REMAINING IN DEEP SPACE',
            effect: () => {
              // Just close the modal - user stays stranded
              setTimeout(() => {
                closeEventModal();
              }, 1000);
            }
          }
        ]
      };
      
      setActiveEvent(fuelDepletionEvent);
    }
    
    // Reset the flag when fuel is no longer depleted (e.g., after refueling)
    if (!fuelDepleted) {
      fuelDepletionHandledRef.current = false;
    }
  }, [fuelDepleted, activeEvent]);

  // Update RandomEventManager conditions - simplified
  useEffect(() => {
    // No complex condition updates needed - just pass combat state for safety
    // The main start/stop logic is handled in NavigationPanel
    if (inCombat) {
      RandomEventManager.stop();
    }
  }, [inCombat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const chainTimeout = chainTimeoutRef.current;
      if (chainTimeout) {
        clearTimeout(chainTimeout);
      }
      // No more complex cleanup needed - EventTypeManager removed
    };
  }, []);

  // Render mini-games if active
  if (showCombatMiniGame || showAsteroidMiniGame) {
    return (
      <MiniGameLauncher
        showAsteroidMiniGame={showAsteroidMiniGame}
        showCombatMiniGame={showCombatMiniGame}
        miniGameConfig={miniGameConfig}
        weapons={weapons}
        showQuantumStorm={showQuantumStorm}
        quantumStormIntensity={quantumStormIntensity}
        onAsteroidComplete={handleAsteroidMiniGameComplete}
        onAsteroidGameOver={handleAsteroidMiniGameOver}
        onAsteroidClose={handleAsteroidMiniGameClose}
        onCombatVictory={handleCombatMiniGameVictory}
        onCombatDefeat={handleCombatMiniGameDefeat}
        onCombatClose={handleCombatMiniGameClose}
        onQuantumStormComplete={() => setShowQuantumStorm(false)}
      />
    );
  }

  // Render alien encounter modal if active
  if (showAlienModal && alienEncounter) {
    return (
      <>
        <AlienEncounterModal
          encounter={alienEncounter}
          onClose={handleAlienModalClose}
          onOutcome={handleAlienOutcome}
        />
        <MiniGameLauncher
          showAsteroidMiniGame={false}
          showCombatMiniGame={false}
          miniGameConfig={null}
          weapons={weapons}
          showQuantumStorm={showQuantumStorm}
          quantumStormIntensity={quantumStormIntensity}
          onQuantumStormComplete={() => setShowQuantumStorm(false)}
        />
      </>
    );
  }

  // Render event modal and quantum storm
  return (
    <>
      <MiniGameLauncher
        showAsteroidMiniGame={false}
        showCombatMiniGame={false}
        miniGameConfig={null}
        weapons={weapons}
        showQuantumStorm={showQuantumStorm}
        quantumStormIntensity={quantumStormIntensity}
        onQuantumStormComplete={() => setShowQuantumStorm(false)}
      />
      <EventModal
        activeEvent={activeEvent}
        actionFeedback={actionFeedback}
        isProcessingAction={isProcessingAction}
        modalFading={modalFading}
        onActionSelect={handleEventAction}
        onClose={closeEventModal}
      />
    </>
  );
};

export default EnhancedWarpEventManager;